import { ApolloQueryResult } from 'apollo-client';

import { addDialogMutation } from '../graphql/mutations/grid/dialogs';
import { balanceCartDataQuery, commonWallet, dealsCount } from '../graphql/queries/grid/common';
import { dialogsQuery } from '../graphql/queries/grid/dialogs';
import { campaignRecommendationsQuery } from '../graphql/queries/grid/recommendations';
import { turboRecomendationQuery } from '../graphql/queries/grid/turbo';
import { mapStringToCurrencySymbol } from '../mappers/common/common';
import { rumCounters } from '../service/RUM/RUM';
import { dealStatusForFilter } from '../typings/deals';
import {
    AddDialogMutation,
    AddDialogMutation_addDialog,
    BalanceCartDataQuery,
    CampaignRecommendationsQuery,
    CampaignRecommendationsQuery_client_campaigns_rowset_recommendations,
    CommonWalletQuery,
    DealsCount,
    DialogsQuery,
    DialogsQuery_client_dialogs,
    GdAddDialogRequestInput,
    GdAdOrderByFieldInput,
    GdStatPresetInput,
    OrderInput,
    TurboRecomendationQuery
} from '../typings/graphql-grid';

import { BaseDataProvider } from './BaseDataProvider/BaseDataProvider';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from './TurboDataProvider/TurboDataProvider';

interface JointAccountResponse {
    sum: number;
    enabled: boolean;
    currency: string;
}

interface BalanceCartDataResult {
    walletId: string;
    isB2bBalanceCartEnabled: boolean;
}

export type NewDealsCountResponse = number;
export type DialogsQueryResponse = (DialogsQuery_client_dialogs | null)[];
export type DialogMutationResponse = AddDialogMutation_addDialog;

export class QueryExecutor extends BaseDataProvider {
    public getCommonWallet(login: string): Promise<JointAccountResponse> {
        return this.client
            .query<CommonWalletQuery>({
                query: commonWallet,
                variables: {
                    login
                }
            })
            .then((result: ApolloQueryResult<CommonWalletQuery>) => {
                const { wallets } = result.data.client;

                const defaultResult = {
                    sum: 0,
                    enabled: false,
                    currency: ''
                };

                if (wallets.length > 1) {
                    return defaultResult;
                }

                return result.data.client.wallets.reduce((acc: JointAccountResponse, item) => {
                    const { enabled } = item.status;

                    acc.enabled = acc.enabled || enabled;

                    if (enabled) {
                        acc.sum = acc.sum + Number(item.sum);
                        acc.currency = acc.currency || mapStringToCurrencySymbol(item.currency);
                    }

                    return acc;
                }, defaultResult);
            });
    }

    public getBalanceCartData(login: string): Promise<BalanceCartDataResult> {
        return this.client
            .query<BalanceCartDataQuery>({
                query: balanceCartDataQuery,
                variables: {
                    login
                }
            })
            .then((result: ApolloQueryResult<BalanceCartDataQuery>) => {
                const { wallets, features } = result.data.client;

                return {
                    walletId: wallets[0].id,
                    isB2bBalanceCartEnabled: features.isB2bBalanceCartEnabled
                };
            });
    }

    public getNewDealsCount(login: string): Promise<NewDealsCountResponse> {
        const dealsFilter = {
            statusContains: [dealStatusForFilter.RECEIVED]
        };

        return this.client
            .query<DealsCount>({
                query: dealsCount,
                variables: {
                    login,
                    dealsFilter
                }
            })
            .then((result: ApolloQueryResult<DealsCount>) => {
                return result.data.client.deals.totalCount;
            });
    }

    public getTurboRecommendation = (login: string, cid: number, bids: number[]) => {
        return this.client
            .query<TurboRecomendationQuery>({
                query: turboRecomendationQuery,
                variables: {
                    login,
                    adsInput: {
                        statRequirements: {
                            preset: GdStatPresetInput.TODAY
                        },
                        orderBy: {
                            order: OrderInput.ASC,
                            field: GdAdOrderByFieldInput.BANNER_TYPE
                        },
                        limitOffset: {
                            offset: DEFAULT_OFFSET,
                            limit: DEFAULT_LIMIT
                        },
                        filter: {
                            adIdIn: bids,
                            campaignIdIn: cid
                        }
                    }
                }
            })
            .then((result: ApolloQueryResult<TurboRecomendationQuery>) => result.data.client.ads.rowset);
    };

    public getDialogs(login: string): Promise<DialogsQueryResponse> {
        return this.client
            .query<DialogsQuery>({
                query: dialogsQuery,
                variables: {
                    login
                }
            })
            .then((result: ApolloQueryResult<DialogsQuery>) => {
                return result.data.client.dialogs;
            });
    }

    public addDialog(login: string, input: GdAddDialogRequestInput): Promise<DialogMutationResponse> {
        return this.client
            .awaitMutate<AddDialogMutation>({
                mutation: addDialogMutation,
                variables: {
                    input
                },
                login
            })
            .then((result: ApolloQueryResult<AddDialogMutation>) => {
                return result.data.addDialog;
            });
    }

    public getCampaignRecommendations = (login: string, campaignId: string) => {
        const query = this.createQuery<CampaignRecommendationsQuery>(
            campaignRecommendationsQuery,
            {
                variables: {
                    login,
                    campaignId
                }
            },
            rumCounters.backendCampaignRecommendations
        );

        return query.then(({ data }) => {
            return data.client.campaigns.rowset.reduce(
                (acc: CampaignRecommendationsQuery_client_campaigns_rowset_recommendations[], campaign) => {
                    return acc.concat(campaign.recommendations || []);
                },
                []
            );
        });
    };
}
